// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import useAllocations from '@hooks/queries/registry/tokenStandard/useAllocations';
import { useCallback, useMemo } from 'react';
import { Allocation } from '@daml.js/splice-api-token-allocation/lib/Splice/Api/Token/AllocationV1';
import { createInstrumentKey, InstrumentKey } from '../../../utils/instrument';

type AllocationKey = string & { __type: 'allocation' };

const KEY_SEP = ';';

export const useObligationsAllocations = () => {
  const allocations = useAllocations();

  const allocationKeyToTransfersSum = useMemo(() => {
    const result: Record<AllocationKey, Record<InstrumentKey, number>> = {};

    (allocations.data ?? []).forEach((it) => {
      const { allocation } = it.view(Allocation);

      const allocationKey = [
        allocation.transferLeg.sender,
        allocation.transferLeg.receiver,
        allocation.settlement.settlementRef.id,
      ].join(KEY_SEP) as AllocationKey;

      if (!result[allocationKey]) {
        result[allocationKey] = {};
      }

      const transfersSum = result[allocationKey];

      const instrumentKey = createInstrumentKey(
        allocation.transferLeg.instrumentId.id,
        allocation.transferLeg.instrumentId.admin,
      );

      transfersSum[instrumentKey] =
        (transfersSum[instrumentKey] ?? 0) + parseFloat(allocation.transferLeg.amount);
    });

    return result;
  }, [allocations]);

  const getTransfersSum = useCallback(
    (sender: string, receiver: string, agreementId: string, settlementObligationId: string) => {
      const allocationKey = [sender, receiver, agreementId, settlementObligationId].join(
        KEY_SEP,
      ) as AllocationKey;

      return allocationKeyToTransfersSum[allocationKey] ?? {};
    },
    [allocationKeyToTransfersSum],
  );

  return {
    getTransfersSum,
  };
};
